import { useContext, useState } from 'react'
import classNames from 'classnames'
import Cookies from 'js-cookie'
import { Aggregated } from '../../Widgets/types'
import { LivePriceContext } from '../../Pages/INStockDetailPage/context'
import ConvertToDeliveryModal from './ConvertToDeliveryModal'
import PrimaryCard from '@/components/Common/PrimaryCard'
import { formatNumberWithRupeeSymbolAndFixDecimals } from '@/root/utils/common/numbers/format-number-to-string'

export interface TLivePrice {
  price: number | null
  percent_change: number | null
  color: string | null
}

function calcOverallReturns(holdings: Aggregated, live_price?: number | null) {
  const avg_price = holdings.avg_price ?? 0
  const quantity = holdings.quantity ?? 1
  const realised_gains = holdings.realised_gains ?? 0
  let overall = holdings.overall_pnl

  if (typeof live_price === 'number') {
    if (holdings.netting_type === 'only_buy') {
      overall = (live_price - avg_price) * quantity
    } else if (holdings.netting_type === 'only_sell') {
      overall = (live_price - avg_price) * quantity
    } else if (holdings.netting_type === 'net_buy') {
      overall = (live_price - avg_price) * quantity + realised_gains
    } else if (holdings.netting_type === 'net_sell') {
      overall = (live_price - avg_price) * quantity + realised_gains
    }
  }
  return overall
}

function calcUnrealisedReturns(
  holdings: Aggregated,
  live_price?: number | null,
) {
  const avg_price = holdings.avg_price ?? 0
  const quantity = holdings.quantity ?? 1
  let unrealised = holdings.unrealised_gains
  if (typeof live_price === 'number') {
    if (holdings.netting_type === 'only_buy') {
      unrealised = (live_price - avg_price) * quantity
    } else if (holdings.netting_type === 'only_sell') {
      unrealised = (live_price - avg_price) * quantity
    } else if (holdings.netting_type === 'net_buy') {
      unrealised = (live_price - avg_price) * quantity
    } else if (holdings.netting_type === 'net_sell') {
      unrealised = (live_price - avg_price) * quantity
    }
  }
  return unrealised
}

export const PositionRow = ({
  data,
  noBorder = false,
}: {
  data: { key: string; value: string | number; className?: string }[]
  noBorder?: boolean
}) => {
  return (
    <div
      className={classNames('mt-1 flex w-full justify-between', {
        'border-b-1 border-grey-light border-dashed': !noBorder,
      })}
    >
      {data.map((item, idx) => {
        return (
          <div
            key={idx}
            className={`${
              item.className ? item.className : 'text-center'
            } py-2`}
          >
            <p className="text-grey mb-1 text-sm">{item.key}</p>
            <p className="text-grey-dark text-sm font-medium">{item.value}</p>
          </div>
        )
      })}
    </div>
  )
}

export default function TodaysPositions({
  data,
  ind_key,
  isFnO,
  showWithoutWrapper,
}: {
  data: Aggregated
  ind_key?: string
  isFnO?: boolean
  showWithoutWrapper?: boolean
}) {
  const livePrice = useContext(LivePriceContext)

  const overallReturns = calcOverallReturns(data, livePrice?.price)

  const unrealisedReturns = calcUnrealisedReturns(data, livePrice?.price)

  const [showCtDModal, setShowCtDModal] = useState(false)

  const member_id = Cookies.get('memberId')

  const row1 = [
    {
      key: 'Buy Qty',
      value: data.buy_qty ?? '-',
      className: 'text-left w-1/3',
    },
    {
      key: 'Avg. Price',
      value:
        formatNumberWithRupeeSymbolAndFixDecimals({
          value: data.buy_avg_price,
        }) ?? '-',
      className: 'text-center w-1/3',
    },
    {
      key: 'Buy Value',
      value:
        formatNumberWithRupeeSymbolAndFixDecimals({
          value: data.buy_val,
        }) ?? '-',
      className: 'text-right w-1/3',
    },
  ]

  const row2 = [
    {
      key: 'Sell Qty',
      value: data.sell_qty ?? '-',
      className: 'text-left w-1/3',
    },
    {
      key: 'Avg. Price',
      value:
        formatNumberWithRupeeSymbolAndFixDecimals({
          value: data.sell_avg_price,
        }) ?? '-',
      className: 'text-center w-1/3',
    },
    {
      key: 'Sell Value',
      value:
        formatNumberWithRupeeSymbolAndFixDecimals({
          value: data.sell_val,
        }) ?? '-',
      className: 'text-right w-1/3',
    },
  ]

  const row3 = [
    {
      key: 'Realised Returns',
      value:
        formatNumberWithRupeeSymbolAndFixDecimals({
          value: data.realised_gains,
        }) ?? '-',
      className: 'text-left w-1/3',
    },
    {
      key: 'Unrealised Returns',
      value:
        formatNumberWithRupeeSymbolAndFixDecimals({
          value: unrealisedReturns,
        }) ?? '-',
      className: 'text-center w-1/3',
    },
    {
      key: 'Net Returns',
      value:
        formatNumberWithRupeeSymbolAndFixDecimals({
          value: overallReturns,
        }) ?? '-',
      className: 'text-right w-1/3',
    },
  ]

  const TPJSX = (
    <>
      {' '}
      <h2 className="mb-2 text-xl font-medium">Today's Position Details</h2>
      <PositionRow data={row1} />
      {data.sell_qty ? <PositionRow data={row2} /> : null}
      <PositionRow data={row3} noBorder />
      {data.quantity && !isFnO && !member_id ? (
        <p
          className="text-ind-blue mt-2 cursor-pointer text-sm font-medium"
          onClick={() => setShowCtDModal(true)}
        >
          Convert to Delivery
        </p>
      ) : null}
      {showCtDModal && ind_key && !isFnO && (
        <ConvertToDeliveryModal
          showModal={showCtDModal}
          ind_key={ind_key}
          onClose={() => setShowCtDModal(false)}
        />
      )}
    </>
  )

  if (showWithoutWrapper) {
    return TPJSX
  }

  return (
    <PrimaryCard containerClass="bg-ind-white my-6 lazy-node">
      {TPJSX}
    </PrimaryCard>
  )
}
